import { existsSync, promises as fs } from "fs"
import path from "path"
import { Config } from "@/src/utils/get-config"
import { Framework, FRAMEWORKS } from "@/src/utils/frameworks"
import { logger } from "@/src/utils/logger"
import { colors } from "@/src/utils/colors"

/**
 * Detect the actual file extension of style files in the project
 */
function detectStyleFileExtension(config: Config): string {
  if (!config?.resolvedPaths?.styles) {
    return "scss"
  }

  const stylesDir = config.resolvedPaths.styles
  const variablesScss = path.join(stylesDir, "_variables.scss")
  const variablesCss = path.join(stylesDir, "_variables.css")

  // Check if CSS version exists (from Bun conversion)
  if (existsSync(variablesCss)) {
    return "css"
  }

  if (existsSync(variablesScss)) {
    return "scss"
  }

  return "scss"
}

/**
 * Get the main CSS file path based on framework
 */
function getMainCssFilePath(
  framework: Framework,
  config: Config
): string | null {
  const frameworkName = framework.name
  const cwd = config.resolvedPaths.cwd

  const hasSrcDir = config.resolvedPaths.components?.includes("/src/")
  const stylesInSrc = config.resolvedPaths.styles?.includes("/src/")

  let possiblePaths: string[] = []

  switch (frameworkName) {
    case FRAMEWORKS["next-app"].name:
      if (hasSrcDir) {
        possiblePaths = [
          path.join(cwd, "src/app/globals.css"),
          path.join(cwd, "src/app/globals.scss"),
        ]
      } else {
        possiblePaths = [
          path.join(cwd, "app/globals.css"),
          path.join(cwd, "app/globals.scss"),
        ]
      }
      break

    case FRAMEWORKS["next-pages"].name:
      if (hasSrcDir) {
        possiblePaths = [
          path.join(cwd, "src/styles/globals.css"),
          path.join(cwd, "src/styles/globals.scss"),
        ]
      } else {
        possiblePaths = [
          path.join(cwd, "styles/globals.css"),
          path.join(cwd, "styles/globals.scss"),
        ]
      }
      break

    case FRAMEWORKS.vite.name:
    case FRAMEWORKS["react-router"].name:
    case FRAMEWORKS["tanstack-start"].name:
      // Vite-based frameworks
      possiblePaths = [
        path.join(cwd, "src/index.css"),
        path.join(cwd, "src/main.css"),
        path.join(cwd, "src/App.css"),
      ]
      break

    case FRAMEWORKS.astro.name:
      possiblePaths = [
        path.join(cwd, "src/styles/main.css"),
        path.join(cwd, "src/styles/global.css"),
      ]
      break

    case FRAMEWORKS.laravel.name:
      possiblePaths = [path.join(cwd, "resources/css/app.css")]
      break

    case FRAMEWORKS.manual.name:
    default:
      // Try common locations
      if (hasSrcDir || stylesInSrc) {
        possiblePaths = [
          path.join(cwd, "src/index.css"),
          path.join(cwd, "src/main.css"),
          path.join(cwd, "src/App.css"),
          path.join(cwd, "src/styles/main.css"),
        ]
      } else {
        possiblePaths = [
          path.join(cwd, "index.css"),
          path.join(cwd, "main.css"),
          path.join(cwd, "styles/main.css"),
        ]
      }
      break
  }

  for (const filePath of possiblePaths) {
    if (existsSync(filePath)) {
      return filePath
    }
  }

  return possiblePaths[0] || null
}

/**
 * Calculate the relative import path from main CSS file to styles directory
 */
function calculateImportPath(mainCssPath: string, stylesDir: string): string {
  const mainCssDir = path.dirname(mainCssPath)
  let relativePath = path.relative(mainCssDir, stylesDir)

  // Normalize to forward slashes for consistency
  relativePath = relativePath.replace(/\\/g, "/")

  // Ensure it starts with ./ or ../
  if (!relativePath.startsWith(".")) {
    relativePath = "./" + relativePath
  }

  // Add trailing slash
  if (!relativePath.endsWith("/")) {
    relativePath += "/"
  }

  return relativePath
}

/**
 * Check if style imports already exist in the file
 */
function hasStyleImports(content: string, fileExtension: string): boolean {
  const variablesImport = new RegExp(
    `@import\\s+['"].*_variables\\.${fileExtension}['"]`,
    "i"
  )
  const keyframesImport = new RegExp(
    `@import\\s+['"].*_keyframe-animations\\.${fileExtension}['"]`,
    "i"
  )

  return variablesImport.test(content) && keyframesImport.test(content)
}

/**
 * Automatically import style files into the main CSS file
 */
export async function autoImportStyles(
  result: {
    filesCreated: string[]
    filesUpdated?: string[]
    filesSkipped?: string[]
  },
  config: Config,
  framework: Framework
): Promise<{ success: boolean; message: string; filePath?: string }> {
  try {
    // Check all file arrays for style files
    // This is because we don't want explicitly depend on filesCreated only
    // As users may have existing files that were updated or skipped
    const allFiles = [
      ...(result.filesCreated || []),
      ...(result.filesUpdated || []),
      ...(result.filesSkipped || []),
    ]

    const hasStyleFiles = allFiles.some(
      (file) =>
        file.includes("_variables.") || file.includes("_keyframe-animations.")
    )

    // Skip if no style files found in any of the arrays
    // I don't think there is a scenario where this would happen, but just in case
    if (!hasStyleFiles) {
      return {
        success: true,
        message: "No style files to import",
      }
    }

    // Detect file extension
    // Since the CLI recently added support for CSS output (from Bun conversion)
    const fileExtension = detectStyleFileExtension(config)
    const mainCssPath = getMainCssFilePath(framework, config)

    if (!mainCssPath) {
      return {
        success: false,
        message: "Could not determine main CSS file location",
      }
    }

    // Check if main CSS file exists
    if (!existsSync(mainCssPath)) {
      return {
        success: false,
        message: `Main CSS file not found at ${mainCssPath}`,
      }
    }

    const mainCssContent = await fs.readFile(mainCssPath, "utf-8")

    if (hasStyleImports(mainCssContent, fileExtension)) {
      return {
        success: true,
        message: "Style imports already exist",
        filePath: mainCssPath,
      }
    }

    const importPath = calculateImportPath(
      mainCssPath,
      config.resolvedPaths.styles
    )

    const imports = [
      `@import '${importPath}_variables.${fileExtension}';`,
      `@import '${importPath}_keyframe-animations.${fileExtension}';`,
    ].join("\n")

    const newContent = `${imports}\n\n${mainCssContent}`

    await fs.writeFile(mainCssPath, newContent, "utf-8")

    return {
      success: true,
      message: "Style imports added",
      filePath: mainCssPath,
    }
  } catch (error) {
    logger.error(
      `Failed to auto-import styles: ${error instanceof Error ? error.message : String(error)}`
    )
    return {
      success: false,
      message: `Failed to auto-import styles: ${error instanceof Error ? error.message : String(error)}`,
    }
  }
}

/**
 * Log the result of auto-importing styles
 */
export function logAutoImportResult(
  result: Awaited<ReturnType<typeof autoImportStyles>>
): void {
  if (!result.success) {
    logger.warn(colors.yellow(`⚠ ${result.message}`))
    return
  }

  if (result.message === "No style files to import") {
    return
  }

  if (result.message === "Style imports already exist") {
    return
  }

  const relativePath = result.filePath
    ? path.relative(process.cwd(), result.filePath)
    : "main CSS file"

  // remove any ../ from relativePath for cleaner output
  const cleanedRelativePath = relativePath.replace(/\.\.\//g, "")

  logger.log(
    `${colors.green("✔")} Style imports automatically added to ${colors.cyan(cleanedRelativePath)}`
  )
  logger.log("")
}
